<script setup lang="ts">
import { computed, type PropType, ref } from 'vue';

import { useElementSize } from '@vueuse/core';

import UiTitle from '@/components/ui/UiTitle.vue';

const props = defineProps({
  title: {
    type: String as PropType<string>,
    default: ''
  },
  inverted: {
    type: Boolean as PropType<boolean>,
    default: false
  },
  oneRowHeight: {
    type: Number as PropType<number>,
    default: 22
  }
});

const bodyElement = ref<HTMLElement>();
const { height: bodyHeight } = useElementSize(bodyElement);

const borderRadius = computed((): number => {
  const rowsCount = Math.floor(bodyHeight.value / props.oneRowHeight);

  switch (rowsCount) {
    case 0:
    case 1:
      return 12;
    case 2:
      return 14;
    case 3:
      return 16;
    case 4:
      return 18;
    default:
      return 20;
  }
});

const classList = computed(
  (): Record<string, boolean> => ({
    'ui-titled-container_inverted': props.inverted
  })
);
</script>

<template>
  <span class="ui-titled-container" :class="classList" :style="`--radius: ${borderRadius}px`">
    <UiTitle
      v-if="props.title"
      class="ui-titled-container__title"
      :level="3"
      size="sm"
      weight="medium"
    >
      {{ props.title }}
    </UiTitle>
    <span ref="bodyElement" class="ui-titled-container__body">
      <slot />
    </span>
  </span>
</template>

<style scoped>
.ui-titled-container {
  display: block;
}

.ui-titled-container__title {
  padding: 8px 16px;
  color: var(--color-secondary-text);
  text-transform: uppercase;
  background-color: var(--color-secondary-contrast);
  border-radius: 12px 12px 0 0;
}

.ui-titled-container__body {
  display: block;
  padding: 8px 16px;
  background-color: var(--color-main-underlay);
  border-radius: var(--radius);
  transition: border-radius var(--animation-micro) var(--animation-effect);
}

.ui-titled-container_inverted .ui-titled-container__body {
  background-color: var(--color-background);
}

.ui-titled-container__body:not(:first-child) {
  border-radius: 0 0 var(--radius) var(--radius);
}
</style>
